/**
 * NeoCore · Nueva póliza contable manual
 *
 * Editor de asientos con partidas balanceadas. Rediseñado post-QA Carlitos:
 *   - CuentaSelect renderizado con createPortal (fix bug del dropdown fuera del row)
 *   - DEBE/HABER como pills en vez de <select> feo
 *   - Input de monto con prefijo Q visible
 *   - Panel de totales lateral con estado visual (balanceado / diff)
 *   - Badge de naturaleza (DEUDORA/ACREEDORA) al lado de la cuenta seleccionada
 *   - Grid layout consistente en cada fila
 *
 * Autorizado por RPC crear_poliza_manual_admin. Migración 0136.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Plus, Trash, MagnifyingGlass, X, CheckCircle, Warning,
  Calendar, PencilSimple, ArrowsLeftRight,
} from '@phosphor-icons/react'
import { fetchAll } from '@/lib/fetchAll'
import { supabase } from '@/lib/supabase'
import { hoyGT } from '../lib/fechas'

type Cuenta = {
  codigo: string
  nombre: string
  naturaleza: 'DEUDORA' | 'ACREEDORA'
  clase: string
}

type Partida = {
  cuenta_codigo: string
  cuenta_nombre?: string
  cuenta_naturaleza?: 'DEUDORA' | 'ACREEDORA'
  tipo: 'DEBE' | 'HABER' | ''
  monto: string
  descripcion: string
}

const empty = (): Partida => ({
  cuenta_codigo: '', cuenta_nombre: '', cuenta_naturaleza: undefined,
  tipo: '', monto: '', descripcion: '',
})

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function NeoCorePolizaManualPage() {
  const nav = useNavigate()
  const { id: editId } = useParams<{ id: string }>()
  const editMode = !!editId
  const [cuentas, setCuentas] = useState<Cuenta[]>([])
  const [fecha, setFecha] = useState<string>(hoyGT())
  const [descripcion, setDescripcion] = useState('')
  const [partidas, setPartidas] = useState<Partida[]>([empty(), empty()])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)
  const [origenAsiento, setOrigenAsiento] = useState<string | null>(null)

  useEffect(() => {
    (async () => {
      // Paginado: el catálogo pasa de 1000 cuentas y PostgREST corta en 1000 (ver lib/fetchAll).
      const listaCuentas = await fetchAll<Cuenta>((from, to) => supabase.from('cuenta_contable')
        .select('codigo, nombre, naturaleza, clase')
        .eq('acepta_partidas', true).eq('activa', true).order('codigo').range(from, to))
      setCuentas(listaCuentas)

      if (editMode && editId) {
        const porCodigo = new Map(listaCuentas.map((c) => [c.codigo, c]))
        const [{ data: a }, { data: ps }] = await Promise.all([
          supabase.from('asiento_contable').select('fecha, descripcion, origen, estado').eq('id', editId).single(),
          supabase.from('partida_asiento').select('cuenta_codigo, tipo, monto, descripcion').eq('asiento_id', editId).order('orden'),
        ])
        if (a) {
          setFecha(a.fecha)
          setDescripcion(a.descripcion ?? '')
          setOrigenAsiento(a.origen as string)
          if (a.estado !== 'vigente') setError('Este asiento no está vigente; no se puede editar.')
        }
        const filas = (ps ?? []).map((p: any) => {
          const c = porCodigo.get(p.cuenta_codigo)
          return {
            cuenta_codigo: p.cuenta_codigo, cuenta_nombre: c?.nombre,
            cuenta_naturaleza: c?.naturaleza, tipo: p.tipo as 'DEBE' | 'HABER',
            monto: String(p.monto), descripcion: p.descripcion ?? '',
          } as Partida
        })
        if (filas.length >= 2) setPartidas(filas)
      }
    })()
  }, [editMode, editId])

  const totales = useMemo(() => {
    const debe = partidas.reduce((s, p) => s + (p.tipo === 'DEBE' ? Number(p.monto || 0) : 0), 0)
    const haber = partidas.reduce((s, p) => s + (p.tipo === 'HABER' ? Number(p.monto || 0) : 0), 0)
    const diff = +(debe - haber).toFixed(2)
    return { debe, haber, diff, balanceado: diff === 0 && debe > 0 }
  }, [partidas])

  const puedeGuardar = useMemo(() => {
    if (!fecha || descripcion.trim().length < 5) return false
    if (partidas.length < 2) return false
    if (!totales.balanceado) return false
    for (const p of partidas) {
      if (!p.cuenta_codigo || !p.tipo || Number(p.monto) <= 0) return false
    }
    return true
  }, [fecha, descripcion, partidas, totales])

  function updatePart(i: number, patch: Partial<Partida>) {
    setPartidas((arr) => arr.map((p, k) => (k === i ? { ...p, ...patch } : p)))
  }

  // ── Carga masiva: pegar desde Excel (pedido Carlitos 10-ago) ──
  // Formato esperado (3 columnas, tab-separadas al copiar de Excel):
  //   CUENTA | DEBE | HABER   (una de las dos con monto, la otra vacía o 0)
  const [pasteOpen, setPasteOpen] = useState(false)
  const [pasteText, setPasteText] = useState('')
  const [pasteErr, setPasteErr] = useState<string | null>(null)

  function procesarPegado() {
    setPasteErr(null)
    const porCodigo = new Map(cuentas.map((c) => [c.codigo, c]))
    const nuevas: Partida[] = []
    const errores: string[] = []
    const lineas = pasteText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
    lineas.forEach((linea, idx) => {
      const cols = linea.split('\t').map((c) => c.trim())
      if (cols.length < 2) { errores.push(`Línea ${idx + 1}: se esperan 3 columnas (cuenta, debe, haber)`); return }
      const [cod, debeRaw = '', haberRaw = ''] = cols
      // Saltar encabezados
      if (idx === 0 && /cuenta/i.test(cod)) return
      const c = porCodigo.get(cod)
      if (!c) { errores.push(`Línea ${idx + 1}: cuenta ${cod} no existe o no acepta partidas`); return }
      const limpiar = (s: string) => Number(s.replace(/[Qq,\s]/g, '') || 0)
      const debe = limpiar(debeRaw)
      const haber = limpiar(haberRaw)
      if (debe > 0 && haber > 0) { errores.push(`Línea ${idx + 1}: no puede tener debe Y haber`); return }
      if (debe <= 0 && haber <= 0) { errores.push(`Línea ${idx + 1}: falta el monto`); return }
      nuevas.push({
        cuenta_codigo: c.codigo, cuenta_nombre: c.nombre, cuenta_naturaleza: c.naturaleza,
        tipo: debe > 0 ? 'DEBE' : 'HABER',
        monto: String(debe > 0 ? debe : haber),
        descripcion: '',
      })
    })
    if (errores.length) { setPasteErr(errores.slice(0, 5).join(' · ')); return }
    if (nuevas.length < 2) { setPasteErr('Se necesitan al menos 2 partidas'); return }
    setPartidas(nuevas)
    setPasteOpen(false)
    setPasteText('')
  }

  function selectCuenta(i: number, c: Cuenta) {
    const current = partidas[i]!
    updatePart(i, {
      cuenta_codigo: c.codigo,
      cuenta_nombre: c.nombre,
      cuenta_naturaleza: c.naturaleza,
      // Autofill tipo si aún no eligió
      tipo: current.tipo || (c.naturaleza === 'DEUDORA' ? 'DEBE' : 'HABER'),
    })
  }

  async function guardar() {
    if (!puedeGuardar) return
    setLoading(true); setError(null); setOkMsg(null)
    // La descripción es la misma para todas las partidas (DEBE y HABER)
    const payload = partidas.map((p) => ({
      cuenta_codigo: p.cuenta_codigo,
      tipo: p.tipo,
      monto: Number(p.monto),
      descripcion: descripcion.trim(),
    }))
    const { data, error } = editMode
      ? await supabase.rpc('editar_poliza_admin', {
          p_asiento_id: editId,
          p_fecha: fecha,
          p_descripcion: descripcion.trim(),
          p_partidas: payload,
          p_moneda: 'GTQ',
        })
      : await supabase.rpc('crear_poliza_manual_admin', {
          p_fecha: fecha,
          p_descripcion: descripcion.trim(),
          p_partidas: payload,
          p_moneda: 'GTQ',
        })
    setLoading(false)
    if (error) { setError(error.message || 'Error al guardar póliza'); return }
    const r = data as { asiento_id: string; numero: number; total_debe: number }
    setOkMsg(`Póliza #${r.numero} ${editMode ? 'actualizada' : 'creada'} por Q ${fmt(r.total_debe)}`)
    setTimeout(() => nav('/neocore/contabilidad'), 1400)
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <button onClick={() => nav(-1)} style={btnLink}>
        <ArrowLeft size={14} /> Volver
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 8, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 500, letterSpacing: -0.2 }}>
            {editMode ? 'Editar póliza' : 'Nueva póliza manual'}
          </h1>
          <p style={{ margin: '4px 0 0', color: 'var(--muted-foreground)', fontSize: 13 }}>
            {editMode
              ? `Editás las partidas del asiento${origenAsiento ? ` (origen ${origenAsiento})` : ''} · queda registrado en audit_log.`
              : 'Asiento con origen MANUAL · queda registrado en audit_log con tu usuario.'}
          </p>
          {editMode && origenAsiento && origenAsiento !== 'MANUAL' && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#9a5a03', background: 'rgba(217,119,6,0.10)', padding: '6px 10px', borderRadius: 6 }}>
              ⚠ Es un asiento {origenAsiento} (generado por el sistema). Podés reclasificar cuentas y ajustar montos; debe quedar balanceado (DEBE = HABER).
            </p>
          )}
        </div>
      </div>

      {/* Grid principal: form + panel totales */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, alignItems: 'start' }}>
        <div>
          {/* Card: datos del asiento */}
          <div style={cardStyle}>
            <SectionTitle>Datos del asiento</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 14 }}>
              <Field label="Fecha" icon={Calendar}>
                <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Descripción" icon={PencilSimple}>
                <input
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Ej: Ajuste depreciación equipo cómputo julio 2026"
                  maxLength={200}
                  style={inputStyle}
                />
              </Field>
            </div>
          </div>

          {/* Card: partidas */}
          <div style={{ ...cardStyle, marginTop: 16, padding: 0, overflow: 'visible' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <SectionTitle style={{ margin: 0 }}>Partidas ({partidas.length})</SectionTitle>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setPasteOpen((v) => !v); setPasteErr(null) }} style={btnGhost}>
                  ⎘ Pegar desde Excel
                </button>
                <button onClick={() => setPartidas((a) => [...a, empty()])} style={btnGhost}>
                  <Plus size={12} /> Agregar fila
                </button>
              </div>
            </div>
            {pasteOpen && (
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', background: 'var(--n-50)' }}>
                <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--muted-foreground)' }}>
                  Copiá desde Excel 3 columnas: <strong>CUENTA · DEBE · HABER</strong> (una
                  de las dos con monto) y pegá acá. Las filas reemplazan las partidas actuales.
                </p>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder={'10110101\t5000\t\n30210102\t\t5000'}
                  rows={6}
                  style={{ ...inputStyle, width: '100%', fontFamily: 'monospace', fontSize: 12.5, resize: 'vertical' }}
                />
                {pasteErr && (
                  <p style={{ margin: '8px 0 0', fontSize: 12.5, color: '#991B1B' }}>{pasteErr}</p>
                )}
                <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => { setPasteOpen(false); setPasteText(''); setPasteErr(null) }} style={btnSecondary}>
                    Cancelar
                  </button>
                  <button onClick={procesarPegado} disabled={!pasteText.trim()} style={btnGhost}>
                    Procesar {pasteText.trim() ? `(${pasteText.trim().split(/\r?\n/).length} líneas)` : ''}
                  </button>
                </div>
              </div>
            )}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '28px minmax(300px, 1fr) 140px 140px 32px',
              gap: 10, padding: '8px 18px', borderBottom: '1px solid var(--border)',
              background: 'var(--n-50, #f4f4f5)',
              fontSize: 10, fontWeight: 600, color: 'var(--muted-foreground)',
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              <span></span>
              <span>Cuenta</span>
              <span style={{ textAlign: 'right' }}>Debe</span>
              <span style={{ textAlign: 'right' }}>Haber</span>
              <span></span>
            </div>
            <div style={{ overflow: 'visible' }}>
              {partidas.map((p, i) => (
                <PartidaRow
                  key={i}
                  index={i}
                  partida={p}
                  cuentas={cuentas}
                  canDelete={partidas.length > 2}
                  onSelectCuenta={(c) => selectCuenta(i, c)}
                  onChange={(patch) => updatePart(i, patch)}
                  onDelete={() => setPartidas((a) => a.filter((_, k) => k !== i))}
                />
              ))}
            </div>
          </div>

          {/* Errores / OK */}
          {error && (
            <div style={{ marginTop: 12, padding: 12, background: '#FEE2E2', color: '#991B1B',
              borderRadius: 8, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Warning size={16} /> {error}
            </div>
          )}
          {okMsg && (
            <div style={{ marginTop: 12, padding: 12, background: '#ECFDF5', color: '#047857',
              borderRadius: 8, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
              <CheckCircle size={16} weight="fill" /> {okMsg}
            </div>
          )}

          {/* Actions */}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => nav(-1)} style={btnSecondary}>Cancelar</button>
            <button
              onClick={guardar}
              disabled={!puedeGuardar || loading}
              style={{
                ...btnPrimary,
                background: puedeGuardar && !loading ? 'var(--brand-600, #FF5B04)' : '#D1D5DB',
                cursor: puedeGuardar && !loading ? 'pointer' : 'not-allowed',
              }}
            >
              {loading ? 'Guardando…' : editMode ? 'Guardar cambios' : 'Publicar póliza'}
            </button>
          </div>
        </div>

        {/* Panel lateral: totales */}
        <div style={{ position: 'sticky', top: 20 }}>
          <div style={{
            ...cardStyle,
            borderTop: `3px solid ${totales.balanceado ? '#10B981' : totales.debe === 0 ? 'var(--border)' : '#EF4444'}`,
          }}>
            <SectionTitle>Totales</SectionTitle>
            <div style={{ display: 'grid', gap: 10 }}>
              <TotalRow label="DEBE" value={totales.debe} />
              <TotalRow label="HABER" value={totales.haber} />
              <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
              {totales.balanceado ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10,
                  background: '#dcfce7', color: '#166534', borderRadius: 8, fontSize: 13, fontWeight: 500 }}>
                  <CheckCircle size={18} weight="fill" /> Asiento balanceado
                </div>
              ) : totales.debe === 0 && totales.haber === 0 ? (
                <div style={{ padding: 10, background: '#f4f4f5', color: 'var(--muted-foreground)',
                  borderRadius: 8, fontSize: 12, textAlign: 'center' }}>
                  Ingresá los montos para ver el balance
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10,
                  background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 13, fontWeight: 500 }}>
                  <Warning size={18} weight="fill" />
                  <div>
                    <div>Descuadrado</div>
                    <div style={{ fontSize: 11, fontWeight: 400, fontFamily: 'monospace' }}>
                      Diferencia: Q {fmt(Math.abs(totales.diff))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 12, padding: 12, fontSize: 11, color: 'var(--muted-foreground)', lineHeight: 1.5 }}>
            <b style={{ color: 'var(--ink)' }}>Tip:</b> al elegir una cuenta, el sistema autocompleta el
            tipo según la naturaleza (DEUDORA→DEBE, ACREEDORA→HABER). Podés cambiarlo si necesitás.
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// Fila de partida
// ═══════════════════════════════════════════════════════════════════

function PartidaRow({ index, partida, cuentas, canDelete, onSelectCuenta, onChange, onDelete }: {
  index: number
  partida: Partida
  cuentas: Cuenta[]
  canDelete: boolean
  onSelectCuenta: (c: Cuenta) => void
  onChange: (patch: Partial<Partida>) => void
  onDelete: () => void
}) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '28px minmax(300px, 1fr) 140px 140px 32px',
      gap: 10, padding: '12px 18px', borderBottom: '1px solid var(--border)',
      alignItems: 'center',
    }}>
      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', fontWeight: 500, fontFamily: 'monospace' }}>
        {String(index + 1).padStart(2, '0')}
      </div>

      {/* Cuenta */}
      <CuentaSelect cuentas={cuentas} value={partida.cuenta_codigo} onPick={onSelectCuenta} />

      {/* Debe */}
      <MontoInput
        value={partida.tipo === 'DEBE' ? partida.monto : ''}
        onChange={(v) => onChange({ tipo: v ? 'DEBE' : partida.tipo, monto: v })}
      />
      {/* Haber */}
      <MontoInput
        value={partida.tipo === 'HABER' ? partida.monto : ''}
        onChange={(v) => onChange({ tipo: v ? 'HABER' : partida.tipo, monto: v })}
      />

      {/* Delete */}
      <button
        onClick={onDelete}
        disabled={!canDelete}
        title={canDelete ? 'Eliminar fila' : 'Mínimo 2 partidas'}
        style={{
          padding: 6, border: 'none', background: 'transparent',
          cursor: canDelete ? 'pointer' : 'not-allowed',
          color: '#EF4444', opacity: canDelete ? 0.7 : 0.2,
          display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4,
        }}
        onMouseEnter={(e) => { if (canDelete) e.currentTarget.style.opacity = '1' }}
        onMouseLeave={(e) => { if (canDelete) e.currentTarget.style.opacity = '0.7' }}
      >
        <Trash size={15} />
      </button>
    </div>
  )
}

// Input de monto con prefijo Q (columna Debe o Haber)
function MontoInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)',
      overflow: 'hidden', height: 34,
    }}>
      <span style={{ padding: '0 8px', color: 'var(--muted-foreground)', fontSize: 12, fontWeight: 500,
        borderRight: '1px solid var(--border)', height: '100%', display: 'flex', alignItems: 'center',
        background: 'var(--n-50, #f4f4f5)' }}>
        Q
      </span>
      <input
        type="number" step="0.01" min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0.00"
        style={{
          flex: 1, border: 'none', outline: 'none', padding: '0 10px',
          fontFamily: 'monospace', fontSize: 13, textAlign: 'right', background: 'transparent',
        }}
      />
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// CuentaSelect · dropdown con portal para no romper por overflow
// ═══════════════════════════════════════════════════════════════════

function CuentaSelect({
  cuentas, value, onPick,
}: { cuentas: Cuenta[]; value: string; onPick: (c: Cuenta) => void }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const selected = cuentas.find((c) => c.codigo === value)

  const filtered = useMemo(() => {
    const term = q.toLowerCase().trim()
    if (!term) return cuentas.slice(0, 100)
    return cuentas
      .filter((c) => c.codigo.includes(term) || c.nombre.toLowerCase().includes(term))
      .slice(0, 100)
  }, [q, cuentas])

  const openMenu = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect()
      setPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: r.width })
    }
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('[data-cuenta-popover]')) return
      if (triggerRef.current?.contains(target)) return
      setOpen(false); setQ('')
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => open ? setOpen(false) : openMenu()}
        style={{
          height: 34, padding: '0 10px', textAlign: 'left', width: '100%',
          border: '1px solid var(--border)', borderRadius: 6,
          background: 'var(--surface)', fontSize: 12, cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
          color: selected ? 'var(--ink)' : 'var(--muted-foreground)',
        }}>
        {selected ? (
          <>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
              <span style={{ fontFamily: 'monospace', color: 'var(--muted-foreground)', fontSize: 11 }}>
                {selected.codigo}
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selected.nombre}
              </span>
            </span>
            <NatBadge naturaleza={selected.naturaleza} />
          </>
        ) : (
          <>
            <span>Seleccionar cuenta…</span>
            <MagnifyingGlass size={12} />
          </>
        )}
      </button>

      {open && pos && createPortal(
        <div data-cuenta-popover style={{
          position: 'absolute', top: pos.top, left: pos.left, width: Math.max(pos.width, 460),
          background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 12px 32px rgba(0,0,0,0.16)', zIndex: 9999,
          maxHeight: 400, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: 10, borderBottom: '1px solid var(--border)',
            display: 'flex', gap: 6, alignItems: 'center' }}>
            <MagnifyingGlass size={14} color="var(--muted-foreground)" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por código o nombre…"
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 13 }} />
            <button onClick={() => { setOpen(false); setQ('') }}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 2 }}>
              <X size={14} />
            </button>
          </div>
          <div style={{ overflow: 'auto', flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 12 }}>
                Sin resultados
              </div>
            ) : filtered.map((c) => (
              <button key={c.codigo}
                onClick={() => { onPick(c); setOpen(false); setQ('') }}
                style={{
                  width: '100%', padding: '8px 12px', textAlign: 'left',
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  fontSize: 12, display: 'flex', gap: 10, alignItems: 'center',
                  borderBottom: '1px solid #f4f4f5',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                <span style={{ fontFamily: 'monospace', color: 'var(--muted-foreground)',
                  minWidth: 110, fontSize: 11 }}>{c.codigo}</span>
                <span style={{ flex: 1 }}>{c.nombre}</span>
                <NatBadge naturaleza={c.naturaleza} />
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════
// UI atoms
// ═══════════════════════════════════════════════════════════════════

function NatBadge({ naturaleza }: { naturaleza: 'DEUDORA' | 'ACREEDORA' }) {
  const isD = naturaleza === 'DEUDORA'
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '1px 6px', borderRadius: 10, fontSize: 9, fontWeight: 500,
      background: isD ? '#dbeafe' : '#f3e8ff',
      color: isD ? '#1e40af' : '#6b21a8',
      textTransform: 'uppercase', letterSpacing: 0.3,
    }}>
      <ArrowsLeftRight size={8} />
      {isD ? 'Deudora' : 'Acreedora'}
    </span>
  )
}

function TotalRow({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase',
        letterSpacing: 0.5, fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 15, fontFamily: 'monospace', fontWeight: 500,
        fontVariantNumeric: 'tabular-nums' }}>
        Q {fmt(value)}
      </span>
    </div>
  )
}

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase',
      letterSpacing: 0.5, fontWeight: 500, marginBottom: 12, ...style,
    }}>{children}</div>
  )
}

function Field({ label, icon: Icon, children }: { label: string; icon?: any; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase',
        letterSpacing: 0.5, fontWeight: 500 }}>
        {Icon && <Icon size={11} />} {label}
      </span>
      {children}
    </label>
  )
}

// ═══════════════════════════════════════════════════════════════════
// styles
// ═══════════════════════════════════════════════════════════════════

const cardStyle: React.CSSProperties = {
  background: 'var(--surface, #fff)',
  border: '1px solid var(--border)',
  borderRadius: 12, padding: 18,
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', border: '1px solid var(--border)',
  borderRadius: 6, fontSize: 13, background: 'var(--surface)',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', height: 34,
}

const btnLink: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '4px 8px', border: 'none', background: 'transparent',
  color: 'var(--muted-foreground)', cursor: 'pointer', fontSize: 12,
}

const btnPrimary: React.CSSProperties = {
  padding: '10px 22px', border: 'none', borderRadius: 8,
  color: '#fff', fontSize: 13, fontWeight: 500,
}

const btnSecondary: React.CSSProperties = {
  padding: '10px 18px', border: '1px solid var(--border)', borderRadius: 8,
  background: 'var(--surface)', cursor: 'pointer', fontSize: 13,
}

const btnGhost: React.CSSProperties = {
  padding: '5px 10px', border: '1px solid var(--border)', borderRadius: 6,
  background: 'var(--surface)', cursor: 'pointer', fontSize: 12,
  display: 'inline-flex', alignItems: 'center', gap: 4,
}
